//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

//Local libs
import Service from '../common/service';
import ActivityModel from './activities-list.model';

/**
 * Service for managing Activities data (Master Data).
 *
 * Provides methods to fetch and manage Activities-related data.
 * Inherits CRUD operations and HTTP utilities from the generic {@link Service} class.
 *
 * @module ActivityService
 */
@Injectable({
  providedIn: 'root'
})
export class ActivityService extends Service<ActivityModel> {

  /**
   * Stores ongoing HTTP request subscriptions.
   * Used to cancel active requests and prevent memory leaks.
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * Constructs an instance of ActivityMasterService.
   *
   * @param {HttpClient} httpClient - The HttpClient service for making HTTP requests.
   */
  constructor(httpClient: HttpClient) {
    super(httpClient, '/blubay/activitymaster/');
  }

  /**
   * Fetches the complete list of Activities (Master Data).
   *
   * @method
   * @returns {Promise<Array<ActivityMasterModel>>} A Promise resolving with an array of ActivityMasterModel objects.
   * @throws {HttpErrorResponse} Throws an HTTP error if the request fails.
   *
   * Description:
   * This method is typically called in {@link ActivityMasterComponent} to retrieve the full Activities list.
   */
  public getAllActivities(): Promise<Array<ActivityModel>> {
    return new Promise<Array<ActivityModel>>((resolve, reject) => {
      const request = this.httpClient
        .get<Array<ActivityModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=1000&order=new`, this.httpOptions)
        .subscribe(
          response => resolve(response),
          (error: HttpErrorResponse) => reject(error)
        );
      this.ongoingRequests.push(request);
    });
  }

  /**
   * Cancels all ongoing HTTP requests by unsubscribing from their observables.
   * Ensures proper cleanup and prevents memory leaks.
   *
   * Typically called in components when they are destroyed to cancel pending requests.
   *
   * @method
   * @returns {void}
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach(request => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
